import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { endOfMonth, endOfYesterday, isBefore, isFuture, subMonths, subWeeks } from "date-fns/esm";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import DatePicker from "@app/component/input/DatePicker";
import { getEventsTotal, getOldestEventDate } from "@app/scene/dashboard/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { getWeekEnd } from "@app/util/dates";

import styles from "./ScrollTo.module.scss";

const messages = defineMessages({
  lastMonth: {
    id: "dashboard.timeline.header.scrollTo.lastMonth",
    defaultMessage: "Last Month",
    description:
      "Last Month quick option in the Scroll To field in the header of Timeline view on the Dashboard page",
  },
  lastWeek: {
    id: "dashboard.timeline.header.scrollTo.lastWeek",
    defaultMessage: "Last Week",
    description:
      "Last Week quick option in the Scroll To field in the header of Timeline view on the Dashboard page",
  },
  scrollTo: {
    id: "dashboard.timeline.header.scrollTo",
    defaultMessage: "Scroll to",
    description: "A scroll to field label in the header of Timeline view on the Dashboard page",
  },
  today: {
    id: "dashboard.timeline.header.scrollTo.today",
    defaultMessage: "Today",
    description:
      "Today quick option in the Scroll To field in the header of Timeline view on the Dashboard page",
  },
  yesterday: {
    id: "dashboard.timeline.header.scrollTo.yesterday",
    defaultMessage: "Yesterday",
    description:
      "Yesterday quick option in the Scroll To field in the header of Timeline view on the Dashboard page",
  },
});

export interface ScrollToProps {
  className?: string;
  onChange?: (date: Date) => void;
  value?: Date;
}

interface Props extends ScrollToProps {
  oldestEventDate?: Date;
  totalEvents: number;
}

const ScrollTo: FC<Props> = ({ className, oldestEventDate, onChange, totalEvents, value }) => {
  const intl = useIntl();

  const [pickerVisible, setPickerVisibility] = React.useState(false);

  const handleDateChange = useCallback(
    (date: Date | null) => {
      onChange?.(date || new Date());
    },
    [onChange]
  );

  const handleSetToday = useCallback(() => {
    setPickerVisibility(false);
    onChange?.(new Date());
  }, [onChange]);

  const handleSetYesterday = useCallback(() => {
    setPickerVisibility(false);
    onChange?.(endOfYesterday());
  }, [onChange]);

  const handleSetLastWeek = useCallback(() => {
    setPickerVisibility(false);
    onChange?.(getWeekEnd(subWeeks(Date.now(), 1)));
  }, [onChange]);

  const handleSetLastMonth = useCallback(() => {
    setPickerVisibility(false);
    onChange?.(endOfMonth(subMonths(Date.now(), 1)));
  }, [onChange]);

  const isDateDisabled = useCallback(
    (date: Date) => {
      return isFuture(date) || (!!oldestEventDate && isBefore(date, oldestEventDate));
    },
    [oldestEventDate]
  );

  return (
    <div className={cn(styles.container, className)}>
      {intl.formatMessage(messages.scrollTo)}
      <DatePicker
        allowClear
        disabled={!totalEvents}
        disabledDate={isDateDisabled}
        format="P"
        onChange={handleDateChange}
        onOpenChange={setPickerVisibility}
        open={pickerVisible}
        renderExtraFooter={() => (
          <div className={styles.buttonGrid}>
            <Button onClick={handleSetToday} type="link">
              {intl.formatMessage(messages.today)}
            </Button>
            <Button onClick={handleSetYesterday} type="link">
              {intl.formatMessage(messages.yesterday)}
            </Button>
            <Button onClick={handleSetLastWeek} type="link">
              {intl.formatMessage(messages.lastWeek)}
            </Button>
            <Button onClick={handleSetLastMonth} type="link">
              {intl.formatMessage(messages.lastMonth)}
            </Button>
          </div>
        )}
        showToday={false}
        value={value}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  oldestEventDate: getOldestEventDate(state),
  totalEvents: getEventsTotal(state),
});

export default connect(mapStateToProps)(ScrollTo);
